import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageSquare, MessageCircle, Mail, Wrench, Settings, Package, Headphones, ArrowRight } from 'lucide-react';
import usePageContent from '../hooks/usePageContent';
import ServiceRequestForm from '../components/ServiceRequestForm';
import useSEO from '../hooks/useSEO';

const ICONS = [Wrench, Settings, Package, Headphones];
const LINKS = [null, null, { to: '/shop?type=accessory', label: 'Shop parts' }, { to: '/knowledge', label: 'Knowledge center' }];

const CHANNELS = [
    { icon: Phone, label: 'Call', value: '(984) 201-0767', href: 'tel:+19842010767', key: 'call' },
    { icon: MessageSquare, label: 'Text', value: '(984) 201-0767', href: 'sms:+19842010767', key: 'text' },
    { icon: MessageCircle, label: 'WhatsApp', value: 'Message us', href: 'https://wa.me/19842010767', key: 'whatsapp' },
    { icon: Mail, label: 'Email', value: 'info@dryicebulls.com', href: 'mailto:info@dryicebulls.com', key: 'email' },
];

const Services = () => {
    const content = usePageContent();
    useSEO({
        title: 'Dry Ice Blaster Service, Repairs & Parts — Dry Ice Bulls Support',
        description: 'Dry ice blasting machine repairs, maintenance, parts and technical support. Text or WhatsApp anytime — we answer within 24 hours.',
    });

    return (
        <div data-testid="services-page">
            <section className="border-b border-neutral-200 bg-neutral-50">
                <div className="container-page pt-32 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-7">
                        <div className="label-caps text-brand-primary mb-6">/ Service & Support</div>
                        <h1 className="font-display font-black uppercase tracking-tighter text-5xl md:text-7xl text-neutral-900">Keep it<br />running.</h1>
                        <p className="mt-6 text-neutral-600 max-w-xl" data-testid="support-intro">{content?.support_intro}</p>
                    </div>
                    <div className="lg:col-span-5 border border-neutral-900 bg-white p-6" data-testid="support-line">
                        <div className="label-caps text-xs text-neutral-500">Support line</div>
                        <p className="mt-1 font-display font-bold text-lg text-neutral-900" data-testid="support-hours">{content?.support_hours}</p>
                        <div className="mt-4 grid grid-cols-2 gap-2">
                            {CHANNELS.map(({ icon: Icon, label, value, href, key }) => (
                                <a key={key} href={href} target={key === 'whatsapp' ? '_blank' : undefined} rel="noreferrer"
                                    onClick={() => window.trackConversion && window.trackConversion(`support_${key}_click`, { location: 'support_page' })}
                                    className={`border border-neutral-200 hover:border-brand-primary p-3 transition-colors ${key === 'email' ? 'col-span-2' : ''}`} data-testid={`support-${key}`}>
                                    <div className="flex items-center gap-2 label-caps text-xs text-brand-primary"><Icon className="h-4 w-4" />{label}</div>
                                    <div className="mt-1 text-sm text-neutral-800 truncate">{value}</div>
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="container-page py-16">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-neutral-200 border border-neutral-200" data-testid="support-services">
                    {(content?.support_services || []).map((s, i) => {
                        const Icon = ICONS[i % ICONS.length];
                        const link = LINKS[i];
                        return (
                            <div key={i} className="bg-white p-7 flex flex-col" data-testid={`support-service-${i}`}>
                                <Icon className="h-6 w-6 text-brand-primary" />
                                <h2 className="mt-4 font-display font-bold uppercase text-xl text-neutral-900">{s.title}</h2>
                                <p className="mt-2 text-sm text-neutral-600 flex-1">{s.text}</p>
                                {link && <Link to={link.to} className="mt-4 inline-flex items-center gap-1.5 label-caps text-xs text-brand-primary">{link.label} <ArrowRight className="h-3.5 w-3.5" /></Link>}
                            </div>
                        );
                    })}
                </div>
            </section>

            <section className="border-t border-neutral-200" id="repair">
                <div className="container-page py-16 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-4">
                        <div className="label-caps text-brand-primary mb-3">/ Request service</div>
                        <h2 className="font-display font-black uppercase text-3xl md:text-5xl text-neutral-900">Tell us what&apos;s wrong</h2>
                        <p className="mt-4 text-neutral-600">Send the model, what&apos;s happening and a photo if you can. A technician replies within 24 hours — faster if your machine is down.</p>
                    </div>
                    <div className="lg:col-span-8"><ServiceRequestForm /></div>
                </div>
            </section>
        </div>
    );
};

export default Services;
